import type {
  DCSContext,
  FlowContextParams,
  FlowContextParamValue,
} from '@highereducation/dynamic-experience';
import type { PostNode } from '@highereducation/wordpress';
import type { ConfigProps } from '@highereducation/wordpress';

export type ArticleContextualDefaults = {
  degree: string;
  category: string;
  subject: string;
};

export type WindowHeContextualDefaults = {
  degree: { slug: string };
  category: { slug: string };
  subject: { slug: string };
};

type MoneyMetaShape = {
  degree?: string | null;
  category?: string | null;
  subject?: string | null;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isOptionalStringOrNull(
  value: unknown,
): value is string | null | undefined {
  return value === undefined || value === null || typeof value === 'string';
}

/** WPGraphQL may return moneyMeta as null or omit it; nodeByUri is loosely typed. */
function isMoneyMetaShape(value: unknown): value is MoneyMetaShape {
  if (!isRecord(value)) {
    return false;
  }
  return (
    isOptionalStringOrNull(value.degree) &&
    isOptionalStringOrNull(value.category) &&
    isOptionalStringOrNull(value.subject)
  );
}

function readMoneyMeta(post: PostNode): MoneyMetaShape | undefined {
  if (!isRecord(post.nodeByUri)) {
    return undefined;
  }

  const moneyMeta: unknown = post.nodeByUri.moneyMeta;
  if (moneyMeta === null || moneyMeta === undefined) {
    return undefined;
  }

  return isMoneyMetaShape(moneyMeta) ? moneyMeta : undefined;
}

function readTenantDefaults(
  siteSettings?: ConfigProps,
): ArticleContextualDefaults {
  return {
    degree: siteSettings?.defaultDegree ?? '',
    category: siteSettings?.defaultCategory ?? '',
    subject: siteSettings?.defaultSubject ?? '',
  };
}

/**
 * Merges WordPress post moneyMeta with tenant monetization defaults (per field).
 * Post values override tenant defaults, matching legacy Supersonic handleResponse.
 */
export function buildArticleContextualDefaults(
  post: PostNode,
): ArticleContextualDefaults {
  const moneyMeta = readMoneyMeta(post);
  const tenant = readTenantDefaults(post.siteSettings);

  return {
    degree: moneyMeta?.degree || tenant.degree || '',
    category: moneyMeta?.category || tenant.category || '',
    subject: moneyMeta?.subject || tenant.subject || '',
  };
}

/**
 * All-or-nothing DCS: yields a context only when all three slugs are present.
 * Used for the explicit `param-degree/category/subject` shortcode override,
 * where a partial triad must fall back rather than send a partial topic scope.
 */
export function toDcsContext(
  defaults: ArticleContextualDefaults,
): DCSContext | undefined {
  const { degree, category, subject } = defaults;
  if (!degree || !category || !subject) {
    return undefined;
  }
  return { degree, category, subject };
}

/**
 * Page-seed DCS for Dynamic Experience auto-population. Unlike
 * {@link toDcsContext}, this seeds whatever subset of the triad the page
 * knows: a degree-only article page yields `{ degree, category: '',
 * subject: '' }` so `context.degree` survives into the flow.
 */
export function toSeedDcsContext(
  defaults: ArticleContextualDefaults,
): DCSContext | undefined {
  const { degree, category, subject } = defaults;
  if (!degree && !category && !subject) {
    return undefined;
  }
  return { degree, category, subject };
}

function readParamString(value: FlowContextParamValue | undefined): string {
  return typeof value === 'string' ? value.trim() : '';
}

/**
 * Builds a DCS context from explicit `param-degree` / `param-category` /
 * `param-subject` shortcode attrs (surfaced in the collected `context.params`
 * bag). All-or-nothing, reusing {@link toDcsContext}: a complete triad yields a
 * `DCSContext` that overrides the page moneyMeta-derived DCS; a partial triad
 * (or non-string values) yields `undefined` so the caller falls back to
 * moneyMeta rather than sending a partial DCS.
 */
export function dcsContextFromParams(
  params: FlowContextParams | undefined,
): DCSContext | undefined {
  if (!params) return undefined;
  return toDcsContext({
    degree: readParamString(params.degree),
    category: readParamString(params.category),
    subject: readParamString(params.subject),
  });
}

/** Legacy window.HE.contextualDefaults shape from apps/site _document.tsx */
export function toWindowHeContextualDefaults(
  defaults: ArticleContextualDefaults,
): WindowHeContextualDefaults {
  return {
    degree: { slug: defaults.degree },
    category: { slug: defaults.category },
    subject: { slug: defaults.subject },
  };
}

export function buildContextualDefaultsFromPost(post: PostNode): {
  defaults: ArticleContextualDefaults;
  dcs: DCSContext | undefined;
  windowHe: WindowHeContextualDefaults;
} {
  const defaults = buildArticleContextualDefaults(post);
  return {
    defaults,
    // Partial-friendly page seed: a degree-only page still seeds
    // `context.degree` so degree-gated rendering matches legacy. The explicit
    // `param-*` override path stays all-or-nothing via `dcsContextFromParams`.
    dcs: toSeedDcsContext(defaults),
    windowHe: toWindowHeContextualDefaults(defaults),
  };
}
